/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import { Spinner } from '@fluentui/react/lib/Spinner';
import { Stack } from '@fluentui/react/lib/Stack';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluProjectCreationSteps } from 'src/clu/verticals/cluProjectCreationModal/CluProjectCreationModal';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { globalMessages } from 'src/core/utils/globalMessages';

export type CluModalFooterProps = {
    isValid?: boolean;
    onNext: () => void;
    isLoading?: boolean;
    onDismiss: () => void;
    onPrevious?: () => void;
    isBackDisabled?: boolean;
    isFooterDisabled?: boolean;
    activeStep: CluProjectCreationSteps;
    nextButtonAutomationId?: string;
};

const messages = defineMessages({
    back: {
        id: 'clu.modalFooter.back',
        defaultMessage: 'Back'
    },
    next: {
        id: 'clu.modalFooter.next',
        defaultMessage: 'Next'
    },
    createProject: {
        id: 'clu.modalFooter.createProject',
        defaultMessage: 'Create'
    }
});

export const CluModalFooter = (props: CluModalFooterProps) => {
    const {
        onNext,
        onDismiss,
        activeStep,
        onPrevious,
        isValid = true,
        isLoading = false,
        isBackDisabled = false,
        isFooterDisabled = false,
        nextButtonAutomationId
    } = props;
    const { localize } = useLocalization();

    const nextBtnText = activeStep === CluProjectCreationSteps.ReviewAndFinish ? localize(messages.createProject) : localize(messages.next);

    return (
        <Stack horizontal horizontalAlign="space-between" tokens={{ padding: 16 }}>
            <Stack wrap horizontal tokens={{ childrenGap: 8 }}>
                {(isBackDisabled || onPrevious) && (
                    <DefaultButton disabled={isBackDisabled || isFooterDisabled} onClick={onPrevious}>
                        {localize(messages.back)}
                    </DefaultButton>
                )}

                <PrimaryButton disabled={isFooterDisabled || !isValid} onClick={onNext} data-automation-id={nextButtonAutomationId}>
                    {nextBtnText}
                </PrimaryButton>

                {isLoading && <Spinner />}
            </Stack>

            <DefaultButton disabled={isFooterDisabled} onClick={onDismiss}>
                {localize(globalMessages.cancel)}
            </DefaultButton>
        </Stack>
    );
};
